// @file Helper for Whiteboard (tools, colors, etc)
import { trackEvent } from '@@/bits/analytics'
import { isAppCapableOf } from '@@/bits/app_can'
import { ContentPickerSource } from '@@/bits/content_picker'
import { isAppUsing } from '@@/bits/flip'
import { FontNameToFontId } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import { setSearchParam } from '@@/bits/location'
import { exportPollResultsAsCSV } from '@@/bits/surface_polls'
import { tailwindFlattenedColors } from '@@/bits/tailwind_config'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { useTldrawMenuClipboardEvents } from '@@/bits/tldraw/tldraw_hooks'
import { handleGroupLinking } from '@@/bits/whiteboard_linking'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import { handleGroupSpecialEffects } from '@@/bits/whiteboard_special_effects'
import type {
  TldrawColorVariantType,
  TldrawDefaultColorStyleColor,
  ToolTailwindColorType,
} from '@@/bits/whiteboard_tools'
import { TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR } from '@@/bits/whiteboard_tools'
import postMessage from '@@/native_bridge/post_message'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import type { WhiteboardCardBackground } from '@@/types'
import type { WhiteboardTextSize } from '@@/types/whiteboard'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import { processedUrl } from '@padlet/vivaldi-client'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { Editor, IndexKey, JsonObject, TLPage, TLPageId, TLRecord, TLShape, TLShapeId } from 'tldraw'
import {
  DefaultColorThemePalette,
  DefaultSizeStyle,
  defaultTldrawOptions,
  getIndexAbove,
  getIndexBelow,
  getIndexBetween,
  PageRecordType,
  TAB_ID,
} from 'tldraw'
import type { InjectionKey, Ref } from 'vue'

const MAX_FRAME_LIMIT = 100

export const MODERATION_TIMEOUT = 5000 // just 5 seconds now for testing

function getCanvasDimensions(): { height: number; width: number } {
  const editor = getEditorObject()
  if (editor == null) {
    return {
      height: CANVAS_HEIGHT,
      width: CANVAS_WIDTH,
    }
  }
  const { height, width } = editor.getViewportPageBounds()
  return {
    height,
    width,
  }
}

function getTailwindBgColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'bg-neon-pink'
    case 'neon-yellow':
      return 'bg-neon-yellow'
    case 'neon-orange':
      return 'bg-neon-orange'
    case 'neon-green':
      return 'bg-neon-green'
    case 'neon-blue':
      return 'bg-neon-blue'
    case 'grey-200':
      return 'bg-grey-200'
    case 'scarlet-500':
      return 'bg-scarlet-500'
    case 'canary-500':
      return 'bg-canary-500'
    case 'park-500':
      return 'bg-park-500'
    case 'oceanic-500':
      return 'bg-oceanic-500'
    case 'oceanic-400':
      return 'bg-oceanic-400'
    case 'grape-200':
      return 'bg-grape-200'
    case 'oceanic-200':
      return 'bg-oceanic-200'
    case 'canary-200':
      return 'bg-canary-200'
    case 'park-200':
      return 'bg-park-200'
    case 'scarlet-200':
      return 'bg-scarlet-200'
    case 'whiteboard-shape-border-grape':
      return 'bg-whiteboard-shape-border-grape'
    case 'grape-500':
      return 'bg-grape-500'
    case 'tangerine-600':
      return 'bg-tangerine-600'
    case 'canary-600':
      return 'bg-canary-600'
    case 'park-600':
      return 'bg-park-600'
    case 'teal-500':
      return 'bg-teal-500'
    case 'dark-text-100':
      return 'bg-dark-text-100'
    case 'light-text-100':
      return 'bg-light-text-100'
    default:
      return null
  }
}

function getTailwindColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'text-neon-pink'
    case 'neon-yellow':
      return 'text-neon-yellow'
    case 'neon-orange':
      return 'text-neon-orange'
    case 'neon-green':
      return 'text-neon-green'
    case 'neon-blue':
      return 'text-neon-blue'
    case 'grey-200':
      return 'text-grey-200'
    case 'scarlet-500':
      return 'text-scarlet-500'
    case 'canary-500':
      return 'text-canary-500'
    case 'park-500':
      return 'text-park-500'
    case 'oceanic-500':
      return 'text-oceanic-500'
    case 'oceanic-400':
      return 'text-oceanic-400'
    case 'grape-200':
      return 'text-grape-200'
    case 'oceanic-200':
      return 'text-oceanic-200'
    case 'canary-200':
      return 'text-canary-200'
    case 'park-200':
      return 'text-park-200'
    case 'scarlet-200':
      return 'text-scarlet-200'
    case 'whiteboard-shape-border-grape':
      return 'text-whiteboard-shape-border-grape'
    case 'grape-500':
      return 'text-grape-500'
    case 'tangerine-600':
      return 'text-tangerine-600'
    case 'canary-600':
      return 'text-canary-600'
    case 'park-600':
      return 'text-park-600'
    case 'teal-500':
      return 'text-teal-500'
    case 'teal-200':
      return 'text-teal-200'
    case 'teal-100':
      return 'text-teal-100'
    case 'scarlet-100':
      return 'text-scarlet-100'
    case 'canary-100':
      return 'text-canary-100'
    case 'whiteboard-shape-fill-park':
      return 'text-whiteboard-shape-fill-park'
    case 'oceanic-100':
      return 'text-oceanic-100'
    case 'grey-100':
      return 'text-grey-100'
    case 'grey-400':
      return 'text-grey-400'
    case 'grey-300':
      return 'text-grey-300'
    case 'dark-text-100':
      return 'text-dark-text-100'
    case 'light-text-100':
      return 'text-light-text-100'
    case 'light-ui-300':
      return 'text-light-ui-300'
    default:
      return null
  }
}

function getTailwindRingColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'ring-neon-pink'
    case 'neon-yellow':
      return 'ring-neon-yellow'
    case 'neon-orange':
      return 'ring-neon-orange'
    case 'neon-green':
      return 'ring-neon-green'
    case 'neon-blue':
      return 'ring-neon-blue'
    case 'grey-200':
      return 'ring-grey-200'
    case 'scarlet-500':
      return 'ring-scarlet-500'
    case 'canary-500':
      return 'ring-canary-500'
    case 'park-500':
      return 'ring-park-500'
    case 'oceanic-500':
      return 'ring-oceanic-500'
    case 'grape-200':
      return 'ring-grape-200'
    case 'oceanic-200':
      return 'ring-oceanic-200'
    case 'canary-200':
      return 'ring-canary-200'
    case 'park-200':
      return 'ring-park-200'
    case 'scarlet-200':
      return 'ring-scarlet-200'
    case 'grape-600':
      return 'ring-grape-600'
    case 'tangerine-600':
      return 'ring-tangerine-600'
    case 'canary-600':
      return 'ring-canary-600'
    case 'park-600':
      return 'ring-park-600'
    case 'dark-text-100':
      return 'ring-dark-text-100'
    case 'light-text-100':
      return 'ring-light-text-100'
    default:
      return null
  }
}

// Map extra colors to the closest tldraw default color style color
// This is for Jamboard shapes color mapping
const EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR: Record<
  TldrawColorVariantType,
  Partial<Record<string, TldrawDefaultColorStyleColor>>
> = {
  highlight: {
    'grey-200': 'black', // Jamboard shape border color
    'canary-500': 'light-red',
  },
  solid: {},
  note: {},
  semi: {
    'oceanic-300': 'blue', // Jamboard shape fill color
    'grape-100': 'violet',
    'park-100': 'green',
    // 'grey-200': 'white',
  },
}

type SolidColor = string
interface HighlightColor {
  srgb: string
  p3: string
}
interface NoteColor {
  fill: string
  text: string
}
function changeTldrawDefaultColorStyleColor(
  color: string,
  variant: 'solid' | 'highlight' | 'note' | 'semi',
  toolColor: ToolTailwindColorType,
): void {
  // For darkmode, we are setting to the same color as light mode since we're ignoring dark mode for now in v1
  // TODO: Add dark mode colorset
  for (const mode of ['lightMode', 'darkMode'] as const) {
    let targetColor: SolidColor | HighlightColor | NoteColor = '#000000'

    if (variant === 'solid' || variant === 'semi') {
      targetColor = tailwindFlattenedColors[toolColor]
    }
    if (variant === 'highlight') {
      targetColor = {
        srgb: tailwindFlattenedColors[toolColor],
        p3: tailwindFlattenedColors[toolColor],
      }
    }
    if (variant === 'note') {
      targetColor = {
        fill: tailwindFlattenedColors[toolColor],
        text: '#000000',
      }
    }
    DefaultColorThemePalette[mode][color][variant] = targetColor
  }
}

function changeAllTldrawDefaultColorStyleColor(): void {
  const variants = Object.keys(TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR) as TldrawColorVariantType[]
  variants.forEach((variant) => {
    const colors = TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]
    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })
  })
  const variantsForExtraColors = Object.keys(
    EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR,
  ) as TldrawColorVariantType[]
  variantsForExtraColors.forEach((variant) => {
    const colors = EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]
    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })
  })
}

// Actions

enum ActionType {
  Undo = 'Undo',
  Redo = 'Redo',
  SendToFront = 'SendToFront',
  SendToBack = 'SendToBack',
  Link = 'Link',
  SpecialEffect = 'SpecialEffect',
  Cut = 'Cut',
  Copy = 'Copy',
  Paste = 'Paste',
  Duplicate = 'Duplicate',
  ToggleFreeze = 'ToggleFreeze',
  Delete = 'Delete',
  SelectAll = 'SelectAll',
  GoToPreviousPage = 'GoToPreviousPage',
  GoToNextPage = 'GoToNextPage',
  Group = 'Group',
  Ungroup = 'Ungroup',
  EditPoll = 'EditPoll',
  OpenPoll = 'OpenPoll',
  ClosePoll = 'ClosePoll',
  ExportPoll = 'ExportPoll',
}

const actionShortcuts = [
  {
    actionType: ActionType.Undo,
    kbd: '$mod+z',
  },
  {
    actionType: ActionType.Redo,
    kbd: '$mod+Shift+z',
  },
  {
    actionType: ActionType.SendToFront,
    kbd: ']',
  },
  {
    actionType: ActionType.SendToBack,
    kbd: '[',
  },
  {
    actionType: ActionType.Group,
    kbd: '$mod+g',
  },
  {
    actionType: ActionType.Ungroup,
    kbd: '$mod+Shift+g',
  },
  {
    actionType: ActionType.Link,
    kbd: 'Shift+l',
  },
  {
    actionType: ActionType.Cut,
    kbd: '$mod+x',
  },
  {
    actionType: ActionType.Copy,
    kbd: '$mod+c',
  },
  {
    actionType: ActionType.Paste,
    kbd: '$mod+v',
  },
  {
    actionType: ActionType.Duplicate,
    kbd: '$mod+d',
  },
  {
    actionType: ActionType.ToggleFreeze,
    kbd: 'Shift+f',
  },
  {
    actionType: ActionType.Delete,
    kbd: 'Backspace',
  },
  {
    actionType: ActionType.SelectAll,
    kbd: '$mod+a',
  },
]

// Adapted from: https://github.com/tldraw/tldraw/blob/v2.1.4/packages/tldraw/src/lib/ui/context/actions.tsx
// Previously tried to set the output of useActions on window but the behavior was very buggy
const handleAction = (
  actionType: ActionType,
  checkCanEdit: (shape: TLShape) => boolean,
  selectedShapes?: TLShape[],
): void => {
  const tldrawEditor = getEditorObject()
  selectedShapes ??= tldrawEditor?.getSelectedShapes() ?? []
  // We only allow actions on the least permissive of the selected objects
  const areAllSelectedShapesEditable = selectedShapes.every(checkCanEdit)

  switch (actionType) {
    case ActionType.Undo: {
      trackEvent('Whiteboard', 'Undo action')
      tldrawEditor?.undo()
      break
    }
    case ActionType.Redo: {
      trackEvent('Whiteboard', 'Redo action')
      tldrawEditor?.redo()
      break
    }
    case ActionType.SendToFront: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Send to front')
      tldrawEditor?.markHistoryStoppingPoint('bring to front')
      tldrawEditor?.bringToFront(selectedShapes)
      break
    }
    case ActionType.SendToBack: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Send to back')
      tldrawEditor?.markHistoryStoppingPoint('send to back')
      tldrawEditor?.sendToBack(selectedShapes)
      break
    }
    case ActionType.Group: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Group objects')
      const groupShapesMark = tldrawEditor?.markHistoryStoppingPoint('group shapes')
      if (groupShapesMark == null) return
      handleGroupLinking(selectedShapes, groupShapesMark)
      if (isAppUsing('sandboxVisualFx') || isAppUsing('sandboxSoundFx')) {
        handleGroupSpecialEffects(
          selectedShapes.map((shape) => shape.id),
          groupShapesMark,
        )
      }
      tldrawEditor?.groupShapes(selectedShapes)
      tldrawEditor?.squashToMark(groupShapesMark)
      break
    }
    case ActionType.Ungroup: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      const ungroupShapesMark = tldrawEditor?.markHistoryStoppingPoint('ungroup shapes')
      if (ungroupShapesMark == null) return
      trackEvent('Whiteboard', 'Ungroup objects')
      tldrawEditor?.ungroupShapes(selectedShapes)
      tldrawEditor?.squashToMark(ungroupShapesMark)
      break
    }
    case ActionType.Cut: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Cut objects')
      tldrawEditor?.markHistoryStoppingPoint('cut')
      void useTldrawMenuClipboardEvents().cut('unknown')
      break
    }
    case ActionType.Copy: {
      trackEvent('Whiteboard', 'Copy objects')
      tldrawEditor?.markHistoryStoppingPoint('copy')
      void useTldrawMenuClipboardEvents().copy('unknown')
      break
    }
    case ActionType.Paste: {
      trackEvent('Whiteboard', 'Paste objects')
      if (isAppCapableOf('nativeWhiteboardPaste')) {
        postMessage({ message_type: 'native_whiteboard_paste' })
        break
      }
      void navigator.clipboard?.read().then((clipboardItems) => {
        void useTldrawMenuClipboardEvents().paste(clipboardItems, 'unknown', tldrawEditor?.inputs.currentPagePoint)
      })
      break
    }
    case ActionType.Duplicate: {
      trackEvent('Whiteboard', 'Duplicate objects')
      tldrawEditor?.markHistoryStoppingPoint('duplicate shapes')
      tldrawEditor?.duplicateShapes(selectedShapes, { x: 20, y: 20 })
      break
    }
    case ActionType.ToggleFreeze: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Toggle freeze')
      tldrawEditor?.markHistoryStoppingPoint('toggle freeze')
      tldrawEditor?.toggleLock(selectedShapes)
      break
    }
    case ActionType.Delete: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Delete objects')
      tldrawEditor?.markHistoryStoppingPoint('delete')
      tldrawEditor?.deleteShapes(selectedShapes)
      break
    }
    case ActionType.SelectAll: {
      trackEvent('Whiteboard', 'Select all')
      if (isAppUsing('sandboxRetainActiveTool')) {
        useWhiteboardStore().selectTool('select')
        tldrawEditor?.setCurrentTool('select')
      }
      tldrawEditor?.markHistoryStoppingPoint('select all')
      tldrawEditor?.selectAll()
      break
    }
    case ActionType.GoToPreviousPage: {
      trackEvent('Whiteboard', 'Go to previous page')
      const previousPage = tldrawEditor
        ?.getPages()
        .find((_page, index, pages) => pages[index + 1]?.id === tldrawEditor?.getCurrentPageId())
      if (previousPage == null) return
      switchFrame(previousPage.id)
      break
    }
    case ActionType.GoToNextPage: {
      trackEvent('Whiteboard', 'Go to next page')
      const nextPage = tldrawEditor
        ?.getPages()
        .find((_page, index, pages) => pages[index - 1]?.id === tldrawEditor?.getCurrentPageId())
      if (nextPage == null) return
      switchFrame(nextPage.id)
      break
    }
    case ActionType.EditPoll: {
      if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
        return
      }

      const shape = selectedShapes[0] as PadletAttachmentShape
      const isPoll = shape.props.isPoll
      const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
      if (isPoll && post != null) {
        trackEvent('Whiteboard', 'Edit poll')
        useSurfacePostsStore().startEditingPost({ postCid: post.cid })
        useSurfaceContentPickerStore().showPostContentPicker()
        useContentPickerStore().pickContentSource({ source: ContentPickerSource.Poll })
      }
      break
    }
    case ActionType.OpenPoll: {
      if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
        return
      }
      const shape = selectedShapes[0] as PadletAttachmentShape
      const isPoll = shape.props.isPoll
      const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
      if (isPoll && post != null) {
        trackEvent('Whiteboard', 'Open poll')
        void useSurfacePostActionStore().reopenPoll({ post })
      }
      break
    }
    case ActionType.ClosePoll: {
      if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
        return
      }
      const shape = selectedShapes[0] as PadletAttachmentShape
      const isPoll = shape.props.isPoll
      const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
      if (isPoll && post != null) {
        trackEvent('Whiteboard', 'Close poll')
        void useSurfacePostActionStore().closePoll({ post })
      }
      break
    }
    case ActionType.ExportPoll: {
      if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
        return
      }
      const shape = selectedShapes[0] as PadletAttachmentShape
      const isPoll = shape.props.isPoll
      const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
      if (isPoll && post != null) {
        trackEvent('Whiteboard', 'Export poll results as CSV')
        void exportPollResultsAsCSV(useSurfaceStore().publicKey, post)
      }
      break
    }
    default:
      break
  }
}

// Frames

const FRAME_ID_QUERY_PARAM = 'frame_id'

interface PageMeta extends JsonObject {
  backgroundType?: BackgroundType
  framePermissionOverride?: FramePermissionOverride
  customBackgroundUrl?: string

  // Intentionally not include `createdAt` and `createdBy`
  // since we don't know who made which page now. We can
  // only know if we track from the beginning.
  lastModifiedBy?: number
  lastModifiedAt?: number
}

/**
 * Determine the new index key based on surrounding pages.
 */
const getNewIndexKey = (pageBefore: TLPage | null, pageAfter: TLPage | null): IndexKey => {
  if (pageBefore != null && pageAfter != null) {
    return getIndexBetween(pageBefore.index, pageAfter.index)
  }
  if (pageBefore != null) {
    return getIndexAbove(pageBefore.index)
  }
  if (pageAfter != null) {
    return getIndexBelow(pageAfter.index)
  }

  throw new Error('Unable to determine new index key; both pageBefore and pageAfter are null')
}

/**
 * Calculate a new index key for moving a frame from one position to another.
 *
 * The fromIndex is used to determine whether the generated index key comes before or after
 * the index key that currently occupies the toIndex.
 *
 * Example:
 * - If moving a frame from index 3 to index 2, generate an IndexKey that is between index 1 and 2.
 * - If moving a frame from index 1 to index 2, generate an IndexKey that is between index 2 and 3.
 */
const calculateIndexKeyForMoveFrame = (fromIndex: number, toIndex: number): IndexKey | null => {
  const editor = getEditorObject()
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }

  const isMovingToLowerIndex = fromIndex > toIndex

  const pageBefore = isMovingToLowerIndex ? pages[toIndex - 1] : pages[toIndex]
  const pageAfter = isMovingToLowerIndex ? pages[toIndex] : pages[toIndex + 1]

  return getNewIndexKey(pageBefore, pageAfter)
}

/**
 * Calculate a new index key for creating a new frame at a specified position (toIndex).
 *
 * Example:
 * - If inserting a new frame at index 2, generate an IndexKey that is between index 1 and 2.
 */
const calculateIndexKeyForNewFrame = (toIndex: number): IndexKey | null => {
  const editor = getEditorObject()
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }

  const pageBefore = pages[toIndex - 1]
  const pageAfter = pages[toIndex]

  return getNewIndexKey(pageBefore, pageAfter)
}

const moveFrame = (frameId: TLPageId, fromIndex: number, toIndex: number): void => {
  const toIndexKey = calculateIndexKeyForMoveFrame(fromIndex, toIndex)
  if (toIndexKey == null) {
    throw new Error('toIndexKey not found')
  }

  const editor = getEditorObject()
  if (editor == null) return

  editor.markHistoryStoppingPoint('moving page')
  editor.updatePage({
    id: frameId,
    index: toIndexKey,
  })
}

function addFrame(destinationIndex: number): void {
  const editor = getEditorObject()
  if (editor == null) return

  const newPageIndexKey = calculateIndexKeyForNewFrame(destinationIndex)
  if (newPageIndexKey == null) {
    return
  }

  const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
  const newPageId = PageRecordType.createId()
  const pagePayload: Partial<TLPage> = {
    name: 'Page',
    index: newPageIndexKey,
    id: newPageId,
    meta: {
      backgroundType: whiteboardFrameBackgroundStore.defaultFrameBackground.backgroundType,
      customBackgroundUrl: whiteboardFrameBackgroundStore.defaultFrameBackground.backgroundUrl,
    },
  }
  editor.createPage(pagePayload)
  editor.markHistoryStoppingPoint('create page')
  if (isAppUsing('sandboxCardNavigation')) {
    switchFrame(newPageId)
  } else {
    editor.setCurrentPage(newPageId)
  }
}

function deleteFrame(frameId: TLPageId): void {
  const editor = getEditorObject()
  if (editor == null) return
  const shapeIdsForPage = editor.getPageShapeIds(frameId)

  editor.markHistoryStoppingPoint('delete page and shapes')
  // We need to batch because deletePage does not automatically delete shapes
  // so they are orphaned in the store and it causes inconsistent undo behavior
  // (probably due to garbage collection)
  editor.run(
    () => {
      editor.deleteShapes([...shapeIdsForPage])
      editor.deletePage(frameId)
    },
    { history: 'record' },
  )
}

function switchFrame(frameId: TLPageId): void {
  const editor = getEditorObject()
  if (editor == null) return
  editor.setCurrentPage(frameId)
  setSearchParam(FRAME_ID_QUERY_PARAM, frameId)
}

// Backgrounds

enum BackgroundType {
  // Standard
  Plain = 'Plain',
  DotGrid = 'DotGrid',
  Grid = 'Grid',
  Ruled = 'Ruled',
  Frosted = 'Frosted',

  // Template
  VennDiagram = 'VennDiagram',
  WeekdaysCalendar = 'WeekdaysCalendar',
  WeeklyCalendar = 'WeeklyCalendar',
  MonthlyCalendar = 'MonthlyCalendar',
  Timeline = 'Timeline',
  OneHundredChart = 'OneHundredChart',
  TwoColumns = 'TwoColumns',
  ThreeColumns = 'ThreeColumns',
  FourColumns = 'FourColumns',
  FiveColumns = 'FiveColumns',
  FourCorners = 'FourCorners',
  Blueprint = 'Blueprint',
  Chalkboard = 'Chalkboard',
  Custom = 'Custom',
}

const STANDARD_BACKGROUND_TYPES = [
  BackgroundType.Plain,
  BackgroundType.Ruled,
  BackgroundType.DotGrid,
  BackgroundType.Grid,
  BackgroundType.Frosted,
]
const TEMPLATE_BACKGROUND_TYPES = [
  BackgroundType.VennDiagram,
  BackgroundType.WeekdaysCalendar,
  BackgroundType.WeeklyCalendar,
  BackgroundType.MonthlyCalendar,
  BackgroundType.Timeline,
  BackgroundType.OneHundredChart,
  BackgroundType.TwoColumns,
  BackgroundType.ThreeColumns,
  BackgroundType.FourColumns,
  BackgroundType.FiveColumns,
  BackgroundType.FourCorners,
]

// Background svgs are uploaded to this bucket:
// https://console.cloud.google.com/storage/browser/padlet-static-production/whiteboard/frame-backgrounds?project=logical-handler-867
const BACKGROUND_TYPE_TO_URL_MAPPING: { [key in BackgroundType]: string } = {
  // 128x128

  [BackgroundType.Plain]: 'https://padlet.net/whiteboard/frame-backgrounds/plain_background.svg',
  [BackgroundType.Grid]: 'https://padlet.net/whiteboard/frame-backgrounds/grid_background.svg',
  [BackgroundType.Ruled]: 'https://padlet.net/whiteboard/frame-backgrounds/ruled_background.svg',
  [BackgroundType.Frosted]: 'https://padlet.net/whiteboard/frame-backgrounds/frosted_glass.svg',

  // 1920x1080

  // For DotGrid, we tried to use 128x128 but the browser makes it slightly deformed at the edges when it repeats
  [BackgroundType.DotGrid]: 'https://padlet.net/whiteboard/frame-backgrounds/dot_background.svg',

  [BackgroundType.VennDiagram]: 'https://padlet.net/whiteboard/frame-backgrounds/venn_diagram.svg',
  [BackgroundType.WeekdaysCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/weekdays_calendar.svg',
  [BackgroundType.WeeklyCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/weekly_calendar.svg',
  [BackgroundType.MonthlyCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/monthly_calendar.svg',
  [BackgroundType.Timeline]: 'https://padlet.net/whiteboard/frame-backgrounds/timeline.svg',
  [BackgroundType.OneHundredChart]: 'https://padlet.net/whiteboard/frame-backgrounds/one_hundred_chart.svg',
  [BackgroundType.TwoColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/two_columns.svg',
  [BackgroundType.ThreeColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/three_columns.svg',
  [BackgroundType.FourColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/four_columns.svg',
  [BackgroundType.FiveColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/five_columns.svg',
  [BackgroundType.FourCorners]: 'https://padlet.net/whiteboard/frame-backgrounds/four_corners.svg',
  [BackgroundType.Custom]: '',

  // From Jamboard
  [BackgroundType.Blueprint]: 'https://padlet.net/whiteboard/frame-backgrounds/blueprint.svg',
  [BackgroundType.Chalkboard]: 'https://padlet.net/whiteboard/frame-backgrounds/chalkboard.svg',
}

const BACKGROUND_TYPE_TO_PREVIEW_URL_MAPPING: { [key in BackgroundType]: string } = {
  ...BACKGROUND_TYPE_TO_URL_MAPPING,
  // We use a separate (smaller) image for the dot grid preview otherwise the dots will not be visible
  [BackgroundType.DotGrid]: 'https://padlet.net/whiteboard/frame-backgrounds/dot_background_preview.svg',
  // We use a custom preview for the frosted glass background.
  [BackgroundType.Frosted]: 'https://padlet.net/whiteboard/frame-backgrounds/frosted_glass_preview.png',
}

function generateCustomBackground(url: string): WhiteboardCardBackground {
  return {
    id: BackgroundType.Custom,
    name: BackgroundType.Custom,
    url,
    thumbnailUrl: url,
    category: BackgroundType.Custom,
    width: 1920,
    height: 1080,
  }
}

// Permissions

enum FramePermissionOverride {
  None = 'None',
  Reader = 'Reader',
  Writer = 'Writer',
  Editor = 'Editor',
}

// We don't need a No access permission level because the visitor gets blocked at the route level instead
// I.e. they do not even load surface
enum PermissionLevel {
  Reader = 'Reader',
  Writer = 'Writer',
  Editor = 'Editor',
  Manager = 'Manager',
  Administrator = 'Administrator',
  Owner = 'Owner',
}

// Refer to table for breakdown of what each permission can do:
// https://whimsical.com/permissions-technical-representation-72rGSxT28JDQhMMGGEtEo6
interface Permissions {
  canRead: boolean
  canWrite: boolean
  canEdit: boolean
  canManage: boolean
  canAdminister: boolean
  canTrash: boolean
}

const FRAME_PERMISSION_OVERRIDE_MAPPING: { [key in FramePermissionOverride]: PermissionLevel | null } = {
  [FramePermissionOverride.None]: null,
  [FramePermissionOverride.Reader]: PermissionLevel.Reader,
  [FramePermissionOverride.Writer]: PermissionLevel.Writer,
  [FramePermissionOverride.Editor]: PermissionLevel.Editor,
}

const PERMISSIONS_MAPPING: { [key in PermissionLevel]: Permissions } = {
  [PermissionLevel.Reader]: {
    canRead: true,
    canWrite: false,
    canEdit: false,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Writer]: {
    canRead: true,
    canWrite: true,
    canEdit: false,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  // Editor permission is used for frame-level overrides
  // Allows editing frame content, but restricts frame management
  // Users can modify existing elements but cannot add, remove, or reorder frames
  [PermissionLevel.Editor]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Manager]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Administrator]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: true,
    canTrash: false,
  },
  [PermissionLevel.Owner]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: true,
    canTrash: true,
  },
}

const setBackgroundType = (frameId: TLPageId, backgroundType: string): void => {
  const tldrawEditor = getEditorObject()
  if (tldrawEditor == null) return
  const selectedFrame = tldrawEditor.getPage(frameId)
  if (selectedFrame != null) {
    tldrawEditor.updatePage({
      ...selectedFrame,
      meta: {
        ...selectedFrame.meta,
        backgroundType,
      },
    })
  }
}

const setCustomBackgroundUrl = (frameId: TLPageId, url: string): void => {
  const tldrawEditor = getEditorObject()
  if (tldrawEditor == null) return
  const selectedFrame = tldrawEditor.getPage(frameId)
  if (selectedFrame != null) {
    tldrawEditor.updatePage({
      ...selectedFrame,
      meta: {
        ...selectedFrame.meta,
        customBackgroundUrl: url,
      },
    })
  }
}

const setUseThemeFrameBackground = (frameId: TLPageId, useThemeFrameBackground: boolean): void => {
  const tldrawEditor = useTldrawEditor().editor
  if (tldrawEditor == null) return
  const selectedFrame = tldrawEditor.getPage(frameId)
  if (selectedFrame != null) {
    tldrawEditor.updatePage({
      ...selectedFrame,
      meta: {
        ...selectedFrame.meta,
        useThemeFrameBackground,
      },
    })
  }
}

function changeFrameBackground(frameId: TLPageId, backgroundType: string, customBackgroundUrl?: string | null): void {
  setBackgroundType(frameId, backgroundType)
  if (customBackgroundUrl != null) {
    const processedBackgroundUrl =
      backgroundType === BackgroundType.Custom
        ? // For custom uploaded backgrounds, process the URL with canvas dimensions
          processedUrl(customBackgroundUrl, { width: CANVAS_WIDTH, height: CANVAS_HEIGHT })
        : // For Airtable backgrounds, store original URL as fallback if background gets removed
          // see https://airtable.com/appU3PF0BLQdMrwqb/tblsM1y7ICXQ2UizD/viwqitv9YZR4VjiRX?blocks=bli5OqswShIlnVxdM
          customBackgroundUrl

    setCustomBackgroundUrl(frameId, processedBackgroundUrl)
  }
}

function changeAllFrameBackgrounds({
  backgroundType,
  backgroundUrl,
}: {
  backgroundType: string
  backgroundUrl: string
}): void {
  const tldrawEditor = getEditorObject()
  if (tldrawEditor == null) return
  const allPages = tldrawEditor.getPages()
  allPages.forEach((page) => {
    changeFrameBackground(page.id, backgroundType, backgroundUrl)
  })
}

function changeAllFrameBackgroundsUsingThemeFrameBackground({
  backgroundType,
  backgroundUrl,
}: {
  backgroundType: string
  backgroundUrl: string
}): void {
  const tldrawEditor = useTldrawEditor().editor
  if (tldrawEditor == null) return
  const allPages = tldrawEditor.getPages()
  const allPagesWithThemeFrameBackgroundMeta = allPages.filter(
    (page): page is TLPage => 'useThemeFrameBackground' in page.meta && page.meta.useThemeFrameBackground === true,
  )
  allPagesWithThemeFrameBackgroundMeta.forEach((page) => {
    changeFrameBackground(page.id, backgroundType, backgroundUrl)
  })
}

function migrateDeprecatedBackgrounds(editor: Editor): void {
  const allPages = editor.getPages()
  allPages.forEach((page) => {
    const backgroundType = page.meta.backgroundType as string
    // Existing frames only have backgroundType set, except for custom backgrounds that are uploaded by users
    const isUsingDeprecatedBackground =
      backgroundType != null && backgroundType !== BackgroundType.Custom && page.meta.customBackgroundUrl == null
    if (!isUsingDeprecatedBackground) return
    const deprecatedBackgroundUrl = BACKGROUND_TYPE_TO_URL_MAPPING[backgroundType]
    // Skip migration if background type is not in BACKGROUND_TYPE_TO_URL_MAPPING
    if (deprecatedBackgroundUrl == null) return

    // For legacy colored backgrounds that share the same background type identifier,
    // we preserve them as custom backgrounds to maintain their appearance
    editor.updatePage({
      id: page.id,
      meta: {
        backgroundType: BackgroundType.Custom,
        customBackgroundUrl: deprecatedBackgroundUrl,
      },
    })
  })
}

const getFramePermissionOverride = (frameId: TLPageId): FramePermissionOverride => {
  const editor = getEditorObject()
  const frame = editor?.getPage(frameId)
  const frameMeta: PageMeta | undefined = frame?.meta
  return frameMeta?.framePermissionOverride ?? FramePermissionOverride.None
}

const getFramePermissions = (frameId: TLPageId): Permissions | null => {
  const framePermissionOverride = getFramePermissionOverride(frameId)
  const permissionLevel = FRAME_PERMISSION_OVERRIDE_MAPPING[framePermissionOverride]
  return permissionLevel != null ? PERMISSIONS_MAPPING[permissionLevel] : null
}

const isShapeApproved = (record: TLRecord | null | undefined): boolean => {
  if (record == null) {
    return true
  }
  // We don't moderate non-shape records
  if (record.typeName !== 'shape') {
    return true
  }

  return typeof record?.meta.approved === 'boolean' ? record.meta.approved : true
}

// Shapes that are linked to wishes (attachments) go through auto moderation.
// Shapes that have text content go through text moderation.
const SHAPES_SUBJECTED_TO_AUTO_MODERATION = ['padlet-attachment-shape']
const SHAPES_SUBJECTED_TO_TEXT_MODERATION = ['text', 'geo', 'note', 'padlet-speech-bubble']

const isShapeSubjectedToAutoModeration = (shape: TLShape): boolean => {
  return SHAPES_SUBJECTED_TO_AUTO_MODERATION.includes(shape.type)
}

const isShapeSubjectedToTextModeration = (shape: TLShape): boolean => {
  return SHAPES_SUBJECTED_TO_TEXT_MODERATION.includes(shape.type)
}

// This is `false` only for shapes awaiting moderation result
const doesShapeHaveAutoModerationResult = (record: TLRecord | null | undefined): boolean => {
  const submittedForModerationAt = record?.meta?.submittedForModerationAt
  // Cases where shape will not have a timestamp
  // 1. Shape was never submitted for moderation (Not part of auto moderation or created before auto moderation was enabled)
  // 2. Auto moderation results have been received. We delete the timestamp.
  // If there is no timestamp, refer to isShapeApproved to render the shape.
  if (submittedForModerationAt == null) {
    return true
  }

  if (typeof submittedForModerationAt === 'number' && Date.now() - submittedForModerationAt > MODERATION_TIMEOUT) {
    // Default to manual moderation after timeout
    return true
  }

  return false
}

const timeoutShapeModeration = (shape: TLShape): void => {
  const editor = getEditorObject()
  editor?.updateShape({
    id: shape.id,
    type: shape.type,
    meta: {
      submittedForModerationAt: null, // Remove the timestamp to timeout moderation
    },
  })
  editor?.selectNone()
}

const approveShape = (shape: TLShape): void => {
  const editor = getEditorObject()
  editor?.updateShape({
    id: shape.id,
    type: shape.type,
    meta: {
      approved: true,
    },
  })
  editor?.selectNone()
}

// #region Embeds
type EmbedSize = 'small' | 'medium' | 'large'
interface EmbedSizeOption {
  title: string
  width: number
  height: number
  key: EmbedSize
}

// 16:9 aspect ratio sizes
const embedSizeOptions: EmbedSizeOption[] = [
  {
    title: __('Small'),
    width: 480,
    height: 320,
    key: 'small',
  },
  {
    title: __('Medium'),
    width: 720,
    height: 480,
    key: 'medium',
  },
  {
    title: __('Large'),
    width: 1080,
    height: 720,
    key: 'large',
  },
]
// #endregion

// #region Attachment Shapes
const isUploadingAttachmentShape = (record: TLRecord | null | undefined): boolean => {
  if (record == null || record.typeName !== 'shape') {
    return false
  }

  return typeof record.meta.isUploaded === 'boolean' && !record.meta.isUploaded
}
const isShapeCreatedInCurrentTab = (record: TLRecord | null | undefined): boolean => {
  if (record == null || record.typeName !== 'shape') {
    return false
  }
  // `TAB_ID` is stored in sessionStorage, i.e. it is unique to each tab
  return record.meta.tabId === TAB_ID
}
// #endregion

/**
 * Gets the center point of a shape in viewport coordinates (screen space).
 * For shapes that are part of a group, returns the center point of the top-most parent group.
 * The returned coordinates take into account the current zoom level and viewport position,
 * making them suitable for positioning UI elements relative to shapes.
 *
 * @param shapeId - The ID of the shape to get the viewport coordinates for
 * @returns The x,y coordinates of the shape's center in screen space, or null if shape not found or editor unavailable
 */
const getShapeOriginInViewport = (shapeId: TLShapeId): { x: number; y: number } | null => {
  const editor = getEditorObject()
  if (editor == null) return null

  let shape = editor.getShape(shapeId)
  if (shape == null) return null

  // Find the top-most group parent by traversing up the hierarchy
  while (isShapeChildOfGroup(shape)) {
    shape = editor.getShape(shape.parentId) as TLShape
  }

  const shapeBounds = editor.getShapeGeometry(shape).getBounds()
  const shapeCenterPoint = {
    x: shape.x + shapeBounds.w / 2,
    y: shape.y + shapeBounds.h / 2,
  }
  const canvasRect = editor.getViewportScreenBounds()
  const zoomLevel = editor.getZoomLevel()

  // Need to take zoomLevel into account when calculating
  // the shape's position in the viewport
  return {
    x: shapeCenterPoint.x * zoomLevel + canvasRect.x,
    y: shapeCenterPoint.y * zoomLevel + canvasRect.y,
  }
}

const deleteShapeMetadata = (shapeId: TLShapeId, keysToRemove: string[]): void => {
  const editor = getEditorObject()
  if (editor == null) return

  const shape = editor.getShape(shapeId)
  if (shape == null) return

  const metadata = { ...shape.meta }

  keysToRemove.forEach((key) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete metadata[key]
  })
  // Tldraw doesn't completely replace meta when updateShape is used, it combines it with the existing meta
  // We need to update the store directly to completely remove meta keys
  editor.store.put([
    {
      ...shape,
      meta: { ...metadata },
    },
  ])
}

const isGroupShape = (shape: TLShape): boolean => {
  if (shape == null) return false
  const editor = useTldrawEditor().editor
  if (editor == null) return false
  return editor.isShapeOfType(shape, 'group')
}

const isShapeChildOfGroup = (shape: TLShape): boolean => {
  if (shape == null) return false
  const editor = useTldrawEditor().editor
  if (editor == null) return false
  const parent = editor.getShape(shape.parentId)
  return parent != null && editor.isShapeOfType(parent, 'group')
}

const getLongPressedShape = (e: LongPressEvent): TLShape | null => {
  if (!(e instanceof LongPressEvent)) return null

  const editor = useTldrawEditor().editor
  if (editor == null) return null

  const longPressedPoint = editor.inputs.currentPagePoint
  const zoomLevel = editor.getZoomLevel()
  let shape = editor.getShapeAtPoint(longPressedPoint, {
    hitLocked: true,
    margin: defaultTldrawOptions.hitTestMargin / zoomLevel,
    renderingOnly: true,
  })
  if (shape == null) return null

  // For grouped shapes, we want to select the top-most parent group
  while (isShapeChildOfGroup(shape)) {
    shape = editor.getShape(shape.parentId) as TLShape
  }
  return shape
}

const getAllShapesInGroup = (groupShape: TLShape): TLShape[] => {
  if (groupShape == null) return []
  const editor = useTldrawEditor().editor
  if (editor == null || !isGroupShape(groupShape)) return []

  const shapes: TLShape[] = []
  const allShapesInPage = editor.getCurrentPageShapes()

  // Recursive helper function to find all nested shapes
  const findNestedShapes = (parentId: TLShapeId): void => {
    const children = allShapesInPage.filter((shape) => shape.parentId === parentId)
    children.forEach((child) => {
      shapes.push(child)
      // Recursively check if this child has its own children
      findNestedShapes(child.id)
    })
  }

  // Start the recursive search from the group shape
  findNestedShapes(groupShape.id)

  return shapes
}

const shouldEnableGroupingForShapes = (shapes: TLShape[]): boolean => {
  // Don't allow grouping if any shape is pending approval
  const hasShapePendingApproval = shapes.some((shape) => !isShapeApproved(shape))
  return !hasShapePendingApproval
}

const freezeAllShapesInGroup = (editor: Editor, groupId: TLShapeId): void => {
  const children = editor.getCurrentPageShapes().filter((shape) => shape.parentId === groupId)
  children.forEach((shape) => {
    editor.updateShape({
      id: shape.id,
      type: shape.type,
      isLocked: true,
    })
  })
}

const unfreezeAllShapesInGroup = (editor: Editor, groupId: TLShapeId): void => {
  const children = editor.getCurrentPageShapes().filter((shape) => shape.parentId === groupId)
  children.forEach((shape) => {
    editor.updateShape({
      id: shape.id,
      type: shape.type,
      isLocked: false,
    })
  })
}

type TldrawFontId = 'draw' | 'mono' | 'sans' | 'serif'
enum FontNamesToTldrawFontIds {
  ShantellSans = 'draw',
  Inter = 'sans',
  CourierPrime = 'mono',
  Lora = 'serif',
}

/**
 * Determines if a shape or group of shapes contains text content.
 * For groups, checks if all shapes within the group are text-based shapes.
 *
 * @param shape - The shape or record to check. Can be either a single shape or a group shape.
 * @returns {boolean} True if the shape(s) contain text content and have font properties, false otherwise.
 */
function isShapeWithText(shape: TLShape | TLRecord): boolean {
  if (shape == null) return false
  const shapes = isGroupShape(shape as TLShape) ? getAllShapesInGroup(shape as TLShape) : [shape]

  const textShapeTypes = ['text', 'padlet-speech-bubble', 'note', 'arrow', 'geo']

  return shapes.every((currentShape) => {
    return (
      currentShape.typeName === 'shape' && 'font' in currentShape.props && textShapeTypes.includes(currentShape.type)
    )
  })
}

/**
 * Filters text shapes from the store based on given criteria
 */
function filterTextShapes(criteria: (shape: TLShape) => boolean): TLShape[] {
  const editor = useTldrawEditor().editor
  if (editor == null) return []
  const storeSnapshot = editor.store.getStoreSnapshot().store
  return Object.values(storeSnapshot).filter(
    (shape): shape is TLShape =>
      shape.typeName === 'shape' && !isGroupShape(shape) && isShapeWithText(shape) && criteria(shape),
  )
}

function getTldrawFontId(fontId: FontNameToFontId): TldrawFontId {
  return {
    [FontNameToFontId.ShantellSans]: 'draw',
    [FontNameToFontId.Inter]: 'sans',
    [FontNameToFontId.CourierPrime]: 'mono',
    [FontNameToFontId.Lora]: 'serif',
  }[fontId]
}

/**
 * Updates theme font metadata for the given shapes
 */
function updateShapesThemeFontMeta({ shapes, useThemeFont }: { shapes: TLShape[]; useThemeFont: boolean }): void {
  if (shapes.length === 0) return
  const editor = useTldrawEditor().editor
  if (editor == null) return

  editor.updateShapes(
    shapes.map((shape) => ({
      id: shape.id,
      type: shape.type,
      meta: { useThemeFont },
    })),
  )
}

function updateFontOfAllShapesWithThemeFont(fontId: FontNameToFontId): void {
  const editor = useTldrawEditor().editor
  if (editor == null) return
  const allTextShapesWithThemeFont = filterTextShapes((shape) => shape.meta.useThemeFont === true)
  const tldrawFontId = getTldrawFontId(fontId)
  if (tldrawFontId == null || allTextShapesWithThemeFont.length === 0) return

  editor.updateShapes(
    allTextShapesWithThemeFont.map((shape) => ({
      ...shape,
      props: {
        ...shape.props,
        font: tldrawFontId,
      },
    })),
  )
}

/**
 * Migrates existing text shapes to use theme font and fixes incorrect theme font metadata.
 * This handles two cases:
 * 1. Legacy shapes without theme font metadata that use Shantell Sans
 * 2. Shapes with incorrect theme font metadata that don't match the current theme font
 */
function migrateExistingTextShapesToUseThemeFont(): void {
  // Migrate legacy shapes without theme font metadata
  const allTextShapesWithoutThemeFontMeta = filterTextShapes(
    (shape) =>
      !('useThemeFont' in shape.meta) &&
      // All existing text shapes use Shantell Sans font
      'font' in shape.props &&
      shape.props.font === getTldrawFontId(FontNameToFontId.ShantellSans),
  )
  if (allTextShapesWithoutThemeFontMeta.length !== 0) {
    updateShapesThemeFontMeta({
      shapes: allTextShapesWithoutThemeFontMeta,
      useThemeFont: true,
    })
  }

  // Fix shapes with incorrect theme font metadata
  const currentThemeFont = getTldrawFontId(useSurfaceStore().fontId)
  const allTextShapesWithWrongThemeFontMeta = filterTextShapes(
    (shape) =>
      'useThemeFont' in shape.meta &&
      shape.meta.useThemeFont === true &&
      'font' in shape.props &&
      shape.props.font !== currentThemeFont,
  )
  if (allTextShapesWithWrongThemeFontMeta.length !== 0) {
    updateShapesThemeFontMeta({
      shapes: allTextShapesWithWrongThemeFontMeta,
      useThemeFont: false,
    })
  }
}

/**
 * Gets a common property value shared across multiple shapes, if all shapes have the same value.
 * Used for determining unified properties like font or size across multiple selected shapes.
 *
 * @param {Object} params - The parameters object
 * @param {TLShape[]} params.shapes - Array of shapes to check
 * @param {('font'|'size')} params.property - The property to check across shapes
 * @returns {string|null} The common property value if all shapes share it, null otherwise
 *
 * @example
 * const shapes = editor.getSelectedShapes();
 * const commonFont = getCommonShapeProperty({
 *   shapes,
 *   property: 'font'
 * });
 */
function getCommonShapeProperty<T>({ shapes, property }: { shapes: TLShape[]; property: 'font' | 'size' }): T | null {
  // Return null for empty arrays or invalid properties
  if (shapes.length === 0 || !['font', 'size'].includes(property)) {
    return null
  }

  // Ensure the first shape has props
  const firstShape = shapes[0]
  if (!('props' in firstShape)) return null

  const propertyValue = firstShape.props[property] as T
  if (propertyValue == null) return null

  // Check if all shapes have the same property value
  const allShapesMatch = shapes.every(
    (shape) => 'props' in shape && property in shape.props && shape.props[property] === propertyValue,
  )

  return allShapesMatch ? propertyValue : null
}

/**
 * Updates the size of the given shapes based on the provided tool ID.
 * For text shapes, resets the scale to 1 to ensure deterministic text sizing.
 * For auto sizing, updates shape metadata instead of using setStyleForSelectedShapes because auto sizing is not a real size.
 *
 * @param {Object} params - The parameters object
 * @param {TLShape[]} [params.shapes] - Array of shapes to update the size for
 * @param {TLDefaultSizeStyle} params.textSize - The size style to apply ('auto' or a valid size value)
 * @returns {void}
 *
 * @example
 * updateShapesTextSize({
 *   shapes: editor.getSelectedShapes(),
 *   textSize: 'm'
 * });
 */
function updateShapesTextSize({ shapes, textSize }: { shapes?: TLShape[]; textSize: WhiteboardTextSize }): void {
  const editor = useTldrawEditor().editor
  if (editor == null || shapes == null || shapes.length === 0) return

  editor.updateShapes(
    shapes.map((shape) => ({
      ...shape,
      // Under the hood, tldraw uses the scale to apply CSS transform on the entire shape, which affects the text size
      // Reset scale to 1 otherwise the text size would not be deterministic.
      props: shape.type === 'text' ? { scale: 1 } : undefined,
      meta: {
        autoSize: textSize === 'auto',
      },
    })),
  )

  if (textSize !== 'auto') {
    editor.setStyleForSelectedShapes(DefaultSizeStyle, textSize)
  }
}

const updatePollShapeSize = async (
  id: TLShapeId,
  {
    maxTries,
    numTries = 0,
  }: {
    /**
     * Number of times to retry if the update didn't succeed.
     * @default `0`
     **/
    maxTries: number
    /**
     * @internal
     * How many times this function has retried.
     * Used to keep track of the state between calls and
     * should not be provided by the caller.
     */
    numTries?: number
  } = { maxTries: 0 },
): Promise<void> => {
  if (numTries > maxTries) return

  const editor = useTldrawEditor().editor
  if (editor == null) return

  const shape = editor.getShape<PadletAttachmentShape>(id)
  if (shape?.props?.isPoll !== true) return

  // Use `setTimeout` to ensure the poll element is re-painted before getting the height.
  // Increase timeout by 10ms for each retry.
  const delay = numTries * 10
  await new Promise((resolve) => setTimeout(resolve, delay))

  const pollElement = document.getElementById(`whiteboard-poll-${id}`)
  const height = pollElement?.scrollHeight

  // Only retry if we can't get the height of the poll element.
  if (height == null) {
    void updatePollShapeSize(id, { maxTries, numTries: numTries + 1 })
    return
  }

  if (shape.props.h === height) return

  editor.updateShape({
    id: shape.id,
    type: shape.type,
    props: {
      h: height,
    },
  })
}

const TldrawAppRefKey: InjectionKey<Ref<HTMLElement>> = Symbol('tldrawAppRef')

export {
  actionShortcuts,
  ActionType,
  addFrame,
  approveShape,
  BACKGROUND_TYPE_TO_PREVIEW_URL_MAPPING,
  BACKGROUND_TYPE_TO_URL_MAPPING,
  BackgroundType,
  calculateIndexKeyForMoveFrame,
  calculateIndexKeyForNewFrame,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  changeAllFrameBackgrounds,
  changeAllFrameBackgroundsUsingThemeFrameBackground,
  changeAllTldrawDefaultColorStyleColor,
  changeFrameBackground,
  deleteFrame,
  deleteShapeMetadata,
  doesShapeHaveAutoModerationResult,
  embedSizeOptions,
  filterTextShapes,
  FontNamesToTldrawFontIds,
  FRAME_ID_QUERY_PARAM,
  FRAME_PERMISSION_OVERRIDE_MAPPING,
  FramePermissionOverride,
  freezeAllShapesInGroup,
  generateCustomBackground,
  getAllShapesInGroup,
  getCanvasDimensions,
  getCommonShapeProperty,
  getFramePermissionOverride,
  getFramePermissions,
  getLongPressedShape,
  getShapeOriginInViewport,
  getTailwindBgColorClass,
  getTailwindColorClass,
  getTailwindRingColorClass,
  getTldrawFontId,
  handleAction,
  isGroupShape,
  isShapeApproved,
  isShapeChildOfGroup,
  isShapeCreatedInCurrentTab,
  isShapeSubjectedToAutoModeration,
  isShapeSubjectedToTextModeration,
  isShapeWithText,
  isUploadingAttachmentShape,
  MAX_FRAME_LIMIT,
  migrateDeprecatedBackgrounds,
  migrateExistingTextShapesToUseThemeFont,
  moveFrame,
  PermissionLevel,
  PERMISSIONS_MAPPING,
  setUseThemeFrameBackground,
  shouldEnableGroupingForShapes,
  STANDARD_BACKGROUND_TYPES,
  switchFrame,
  TEMPLATE_BACKGROUND_TYPES,
  timeoutShapeModeration,
  TldrawAppRefKey,
  unfreezeAllShapesInGroup,
  updateFontOfAllShapesWithThemeFont,
  updatePollShapeSize,
  updateShapesTextSize,
  updateShapesThemeFontMeta,
}
export type { EmbedSize, EmbedSizeOption, PageMeta, Permissions, TldrawFontId }
